// client/src/pages/PTO/ptoUtils.ts
// Pure utility functions for the PTO Dashboard.

import type { EntryType, PTOEntry, PTOEmployee } from './ptoTypes';
import { ENTRY_TYPES, OFFICES } from './ptoConstants';

// ─── Conflict detection ────────────────────────────────────────────────────

/** Entry types that count as "out of office" for double-booking purposes. */
const OUT_LIKE_TYPES: Set<EntryType> = new Set([
  'PTO', 'Tentative PTO', 'Holiday', 'Sabbatical', 'Half Day',
  'Appointment', 'Flexing Off',
]);

/** True if two HH:MM time windows overlap (half-open: [start, end)). */
function timesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart < bEnd && aEnd > bStart;
}

export interface ConflictCandidate {
  entryType: EntryType;
  entryDate: string;
  startTime?: string | null;
  endTime?: string | null;
}

/**
 * Returns the first existing entry that conflicts with `candidate` for the given
 * employee, or null. Two out-like entries on the same date conflict when either
 * is full-day or their time windows overlap. Non-out-like types (Work, Friday
 * Work, Deadline, Other) never trigger a conflict.
 */
export function findEntryConflict(
  candidate: ConflictCandidate,
  staffIndex: string,
  existing: PTOEntry[],
  excludeId: string | null = null,
): PTOEntry | null {
  if (!OUT_LIKE_TYPES.has(candidate.entryType)) return null;
  const cStart = candidate.startTime || null;
  const cEnd = candidate.endTime || null;
  const cHasWindow = Boolean(cStart && cEnd);

  for (const e of existing) {
    if (excludeId && e.id === excludeId) continue;
    if (e.staffIndex !== staffIndex) continue;
    if (e.entryDate !== candidate.entryDate) continue;
    if (!OUT_LIKE_TYPES.has(e.entryType)) continue;

    const eHasWindow = Boolean(e.startTime && e.endTime);
    if (!cHasWindow || !eHasWindow) return e; // any full-day → conflict
    if (timesOverlap(cStart!, cEnd!, e.startTime!, e.endTime!)) return e;
  }
  return null;
}

// ─── Date helpers ────────────────────────────────────────────────────────────

/** Returns the Monday of the week containing `date`. */
export function mondayOf(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Returns an array of 5 YYYY-MM-DD strings (Mon–Fri) starting from `monday`. */
export function weekDatesFor(monday: Date): string[] {
  const dates: string[] = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(toIso(d));
  }
  return dates;
}

/** Returns 7 YYYY-MM-DD strings (Mon–Sun) for the full week — used for banner matching. */
export function weekDatesForFull(monday: Date): string[] {
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(toIso(d));
  }
  return dates;
}

/** Day-of-week label from ISO ('Mon', 'Sat', etc.) — full name in single form. */
export function isWeekend(iso: string): boolean {
  const [y, m, d] = iso.split('-');
  const dow = new Date(Number(y), Number(m) - 1, Number(d)).getDay();
  return dow === 0 || dow === 6;
}

/** Returns YYYY-MM-DD for each Mon-Fri from startIso to endIso inclusive. */
export function workdaysBetween(startIso: string, endIso: string): string[] {
  if (startIso > endIso) return [];
  const [sy, sm, sd] = startIso.split('-').map(Number);
  const [ey, em, ed] = endIso.split('-').map(Number);
  const cur = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const out: string[] = [];
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) out.push(toIso(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Tax-deadline dates for a year (3/15, 4/15, 9/15, 10/15), bumped to next weekday if weekend. */
export function deadlineDatesForYear(year: number): { date: string; name: string }[] {
  const targets: { month: number; day: number; name: string }[] = [
    { month: 3, day: 15, name: 'March 15 Deadline' },
    { month: 4, day: 15, name: 'April 15 Deadline' },
    { month: 9, day: 15, name: 'September 15 Deadline' },
    { month: 10, day: 15, name: 'October 15 Deadline' },
  ];
  return targets.map(({ month, day, name }) => {
    const d = new Date(year, month - 1, day);
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    return { date: toIso(d), name };
  });
}

/** Format "2026-01-05" as "1/5/2026". */
export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
}

/** Format "2026-01-05" as "Monday, January 5, 2026". */
export function formatDateFull(iso: string): string {
  const [y, m, d] = iso.split('-');
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Convert "14:30" to "2:30 PM". Returns '' if null/empty. */
export function formatTime12(time24: string | null): string {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  let h = Number(hStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${suffix}`;
}

/** Day abbreviation from ISO string: "Mon", "Tue", etc. */
export function dayAbbr(iso: string): string {
  const [y, m, d] = iso.split('-');
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

// ─── Heat map ────────────────────────────────────────────────────────────────

/** Returns 0–4 intensity for a count of PTO days. */
export function getHeatLevel(days: number): number {
  if (days <= 0) return 0;
  if (days <= 2) return 1;
  if (days <= 4) return 2;
  if (days <= 6) return 3;
  return 4;
}

// ─── Style lookups ───────────────────────────────────────────────────────────

/** Returns `{ bg, color }` for the given entry type. */
export function getEntryTypeStyle(type: EntryType): { bg: string; color: string } {
  const match = ENTRY_TYPES.find((t) => t.value === type);
  return match
    ? { bg: match.bgColor, color: match.textColor }
    : { bg: 'rgba(167,179,196,.15)', color: '#a7b3c4' };
}

/** Returns the office badge color string. */
export function getOfficeColor(office: string): string {
  const match = OFFICES.find((o) => o.value === office);
  return match ? match.color : '#8090a0';
}

/** Alphabetical comparator for staff picklists (locale- and case-aware). */
export function compareByName(a: PTOEmployee, b: PTOEmployee): number {
  return a.displayName.localeCompare(b.displayName);
}

// ─── Internal ────────────────────────────────────────────────────────────────

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
